import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { useLeaveRequests } from '../leave/hooks.js';
import { HomeCard } from './HomeCard.js';
import { formatDateOnlyShort } from './useToday.js';

const DATE_FORMAT: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
const SHOWN = 3;

function range(start: string, end: string): string {
  return start === end
    ? formatDateOnlyShort(start, DATE_FORMAT)
    : `${formatDateOnlyShort(start, DATE_FORMAT)} - ${formatDateOnlyShort(end, DATE_FORMAT)}`;
}

/**
 * The caller's OWN leave requests still awaiting a decision. `GET /leave/requests` returns
 * everything in the caller's visible scope (a manager sees their team's too), so the person filter
 * here is what keeps this "My" pending leaves.
 */
export function PendingLeavesCard() {
  const { me } = useAuthenticatedSession();
  const query = useLeaveRequests();
  const pending =
    query.data?.status === 200
      ? query.data.body.items.filter((r) => r.status === 'pending' && r.personId === me.person.id)
      : [];

  return (
    <HomeCard
      title="My Pending Leaves"
      tag={
        pending.length > 0 ? (
          <span className="flex size-5 items-center justify-center rounded-full bg-avatar-7-bg text-[10px] font-bold text-avatar-7-fg">
            {pending.length}
          </span>
        ) : undefined
      }
      viewAll={{ to: '/leave?tab=requests', label: 'View all' }}
      query={{ isPending: query.isPending, isError: query.isError }}
      isEmpty={pending.length === 0}
      emptyTitle="No pending leave requests"
      emptyDescription="Requests waiting on approval will show here."
    >
      <div className="flex flex-col gap-3">
        {pending.slice(0, SHOWN).map((request) => (
          <Link
            key={request.id}
            to="/leave?tab=requests"
            className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3 shadow-xs transition-all hover:border-border-strong"
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-subtle text-accent">
              <CalendarDays className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-fg">{request.leaveTypeName}</p>
              <p className="truncate text-[10px] text-fg-subtle">
                {range(request.startDate, request.endDate)} ({request.days}{' '}
                {Number(request.days) === 1 ? 'day' : 'days'})
              </p>
            </div>
            <span className="rounded-full bg-warning-subtle px-2.5 py-0.5 text-[10px] font-semibold text-warning">
              Pending
            </span>
          </Link>
        ))}
      </div>
    </HomeCard>
  );
}
